import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureFlagsForm } from '@/backoffice/features/tenants/components/feature-flags/feature-flags-form';
import {
  TFeatureFlag,
  TFeatureFlagsDrawerContentProps,
  TFeatureFlagsFormSchema,
} from '@/backoffice/features/tenants/components/feature-flags/feature-flags.types';
import { featureFlagsFormSchema } from '@/backoffice/features/tenants/components/feature-flags/feature-flags.validation';
import {
  useReadSuperTenantQuery,
  useReadTenantFeatureFlagsQuery,
  useUpdateBackofficeTenantMutation,
} from '@/backoffice/redux/api/backoffice/backoffice-api';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { Loader } from '@/shared/components/ui/loader/loader';
import { showToastError, showToastSuccess } from '@/shared/utils/toast';

export const FeatureFlagsDrawerContent: React.FC<
  TFeatureFlagsDrawerContentProps
> = ({ tenantId, handleClose }) => {
  const { t } = useTranslation();
  const { data: superTenantData, isLoading: isCatalogLoading } =
    useReadSuperTenantQuery();
  const { data: tenantData, isLoading: isTenantLoading } =
    useReadTenantFeatureFlagsQuery({ tenantId });
  const [updateTenant] = useUpdateBackofficeTenantMutation();

  const objectVersion = tenantData?.readTenant.objectVersion;

  const flags = useMemo<TFeatureFlag[]>(() => {
    const available = superTenantData?.readSuperTenant.featureFlags ?? [];
    const current = tenantData?.readTenant.featureFlags ?? [];

    return available.map((flag) => ({
      featureName: flag.featureName,
      description: flag.description ?? '',
      isEnabled:
        current.find(
          (currentFlag) => currentFlag.featureName === flag.featureName,
        )?.isEnabled ?? false,
    }));
  }, [superTenantData, tenantData]);

  const handleSubmit = async (
    data: TSubmittedData<TFeatureFlagsFormSchema>,
  ) => {
    try {
      // The backend replaces the whole featureFlags object, so we send the
      // full set on every save and only include the enabled flags — omitted
      // flags are removed (i.e. disabled).
      const enabledFlags = flags
        .filter((_, index) => data.data.flags[index].isEnabled)
        .map((flag) => ({ featureName: flag.featureName, isEnabled: true }));

      await updateTenant({
        tenantId,
        tenant: {
          featureFlags: enabledFlags,
          objectVersion: objectVersion != null ? objectVersion + 1 : undefined,
        },
      }).unwrap();
      showToastSuccess(t('backoffice:feature_flags.update_success'));
      handleClose();
    } catch {
      showToastError(t('backoffice:feature_flags.update_error'));
    }
  };

  if (isCatalogLoading || isTenantLoading) {
    return <Loader />;
  }

  return (
    <Form<TFeatureFlagsFormSchema>
      schema={featureFlagsFormSchema}
      defaultValues={{ flags }}
      onSubmit={handleSubmit}
    >
      {() => <FeatureFlagsForm flags={flags} handleClose={handleClose} />}
    </Form>
  );
};
